// The repository page's Branches tab (docs/design.md "Repository page"):
// a repository's branches, grouped (logic/branches.ts), each pull
// request's whose turn (the dashboard's attention set), and how much of
// each branch the viewer has already reviewed (logic/branchReviewed.ts).
//
// What it reads of GitHub, in two phases:
//
// 1. The listing: the default branch's name (`repos/:o/:r`, revalidated by
//    ETag, as a compare without a base reads it), then every branch's name
//    and tip (`REPO_BRANCH_LIST`, 100 a page with no nested connection,
//    one point a page; MAX_LIST_PAGES at most), sorted here newest push
//    first, since GitHub lists refs by name.
// 2. The details (`branchDetailsQuery`: checks, open and merged pull
//    requests, standing against the default branch), BRANCH_DETAILS_BATCH
//    branches a query (one point), for the branches the groups above Stale
//    are made of (the default branch, then the viewer's own branches pushed
//    within STALE_DAYS, then the others pushed within it, newest first,
//    EAGER_DETAILS at most), and for any other as its row comes on screen
//    (`see`). A batch GitHub runs out of time on is asked again in halves,
//    and a single branch without its comparison, the costly part, so one
//    slow comparison costs only its own row's standing; any other failure
//    fails the batch at once. Each page of the listing is shown as it
//    arrives.
//
// Then, for the open pull requests found, their attention sets as the
// dashboard works them out (its rows by ID, `readRows`, and
// `workOutAttention` with the inputs it keeps), and, for a row on screen,
// once per tip and base, its reviewed share: GitHub's comparison by SHA
// of its base (a pull request's base branch, else the default branch) and
// its tip (`compare/<sha>...<sha>`, never changing, so kept for the page's
// life by those SHAs), the old blobs at the merge base (trackBlobs, a
// hundred paths a query), the statuses the compare or pull request page
// kept for its files (reviewStatus), then the repository's marks on those
// blobs (MarksStore with no pull request, as a compare's page keeps them).
//
// Live: marks notices of the repository look up again the blobs shown, so
// a mark made in another tab moves the figure. The branch list is read
// when the tab opens and when asked; it is not refreshed live.
import { coreBudgetLeft, graphql, graphqlResult, GraphQLFailure, rest } from '../api/gh';
import { sameLogin } from '../logic/attention';
import { HttpError, LruMap } from '../api/cache';
import { failureOf, type Failure } from '../api/failure';
import { onIdentityChange } from '../api/identity';
import { BRANCH_DETAILS_BATCH, branchDetailsQuery, REPO_BRANCH_LIST, type DashPr } from '../api/queries';
import { LiveEvents, type Notice } from '../api/sse';
import { eng } from '../diff/engine';
import { algorithmOf } from '../diff/algorithms';
import type { AttentionMember } from '../engine/types';
import { byNewest, groupBranches, readDetail, readListed, STALE_DAYS, type Branch, type BranchDetail, type BranchGroup } from '../logic/branches';
import { LISTED_FILES_MAX, oldSides, reviewedShare, sidesShown, type BranchFile, type ReviewedShare, type ShareInputs, type Unlisted } from '../logic/branchReviewed';
import { subjectKey, type Subject } from '../subject';
import { CompareNotFound, defaultBranch } from './compare/fetch';
import { sharedAttentionInputs } from './attentionInputs';
import { CheckQueue, workOutAttention } from './dashboard/attention';
import { readRows } from './dashboard/search';
import { MarksStore, type MarksNotice } from './marks.svelte';
import { prefs } from './prefs.svelte';
import { idbStatusStore, statusKeyOf, type FileStatus } from './reviewStatus';
import { session } from './session.svelte';
import { blobsOnTrack, blobsOnTrackPerPath, githubSource, uncachedChanges } from './trackBlobs';

/** Pages of the listing read at most: 1,000 branches. */
export const MAX_LIST_PAGES = 10;
/** Branches whose details are read without waiting for their rows (the newest pushed within STALE_DAYS). */
export const EAGER_DETAILS = 100;
/** Commits from a comparison's newest end whose change lists a lookup of earlier versions reads: where the version the viewer last reviewed sits. */
export const NEWEST_COMMITS = 20;
/** REST requests one view of the tab's lookup of earlier versions may send: the pages of newest commits, and each change list no page kept (two for a merge). */
export const CHANGE_READ_BUDGET = 100;
/**
 * Below this much of the hour's REST budget left (`coreBudgetLeft`: what
 * GitHub's latest answer reported, less what was sent since), no lookup of
 * earlier versions is made. An unknown budget (no answer has reported one:
 * GitHub Enterprise with rate limits off sends none) does not stop it: the
 * view's CHANGE_READ_BUDGET still bounds it, and the row's own compare read,
 * just before, reports the budget wherever GitHub keeps one.
 */
export const CORE_FLOOR = 1000;

/** The listing, as the store keeps it. */
export interface BranchList {
  branches: Branch[];
  /** The cursor of the next page; null on the last. */
  next: string | null;
  /** Branches in the repository, as GitHub counts them. */
  total: number;
}

/** A commit of a comparison: its SHA, and whether it is a merge (its change list then costs two requests: the commit, and a compare with its first parent). */
export interface CommitPoint {
  sha: string;
  merge: boolean;
}

/**
 * The pages of a comparison of `total` commits, `per` a page, that hold its
 * newest `per` commits, oldest first: GitHub lists a comparison oldest
 * first, so they are its last page and, when that one is partial, the one
 * before.
 */
export function lastPages(total: number, per = NEWEST_COMMITS): number[] {
  if (total <= per) return [1];
  const last = Math.ceil(total / per);
  return total % per ? [last - 1, last] : [last];
}

/** A branch's comparison with its base, as its reviewed share reads it. */
export interface Compared {
  mergeBase: string;
  files: BranchFile[];
  /** Its first page's commits, oldest first (NEWEST_COMMITS at most): all of them on a short branch. */
  commits: CommitPoint[];
  /** Its commits, as GitHub counts them. */
  total: number;
}

/** A pull request's attention set as the dashboard works it out, and its row (who it was asked of, teams too). */
export interface PullAttention {
  pr: DashPr;
  /** Null when it could not be worked out. */
  members: AttentionMember[] | null;
}

/** What the store reads: GitHub by default; tests give fakes. */
export interface BranchesWorld {
  defaultBranch(owner: string, repo: string, signal: AbortSignal): Promise<string>;
  /** A page of the listing after cursor `after` (null: the first); null when GitHub shows no such repository. */
  list(owner: string, repo: string, after: string | null, signal: AbortSignal): Promise<BranchList | null>;
  /** The details of the branches at `tips` (name → tip SHA), by name (`gone` for a branch GitHub no longer has); without the comparison unless `compare`. */
  details(owner: string, repo: string, def: string, tips: ReadonlyMap<string, string>, compare: boolean, signal: AbortSignal): Promise<Map<string, BranchDetail>>;
  /** The attention sets of the pull requests with node IDs `ids`. */
  attention(ids: string[], signal: AbortSignal): Promise<Map<string, PullAttention>>;
  /** The comparison of commit `base` with commit `head`. */
  compare(owner: string, repo: string, base: string, head: string, signal: AbortSignal): Promise<Compared>;
  /** The blob of each of `paths` at commit `at`, where it has one. */
  baseBlobs(owner: string, repo: string, at: string, paths: readonly string[]): Promise<Map<string, string>>;
  /** Every (path, blob) each of `paths` had at `points` (trackBlobs, as the compare page asks a file's marks), by every name it had. */
  trackPairs(owner: string, repo: string, points: readonly string[], paths: readonly string[], at: string): Promise<{ path: string; blob: string }[]>;
  /** Page `page` (NEWEST_COMMITS a page) of the commits of the comparison of `base` with `head`, oldest first: one REST request. */
  comparePage(owner: string, repo: string, base: string, head: string, page: number, signal: AbortSignal): Promise<CommitPoint[]>;
  /** Which of `shas` have no change list kept: the REST requests reading their blobs on a track would make. No GitHub call. */
  uncachedChanges(owner: string, repo: string, shas: readonly string[]): Promise<string[]>;
  /** What is left of the hour's REST budget; null when not known. */
  coreLeft(): number | null;
  /** The statuses the compare or pull request page kept for `subject`'s files as it opens, by file identity. */
  kept(subject: Subject): Promise<ReadonlyMap<string, FileStatus>>;
  listen(topics: string[], handler: (topic: string, notice: Notice | null) => void): () => void;
}

interface RawFile {
  filename?: unknown;
  previous_filename?: unknown;
  status?: unknown;
  sha?: unknown;
  additions?: unknown;
  deletions?: unknown;
  patch?: unknown;
}

const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const count = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const points = (commits: { sha?: unknown; parents?: unknown[] }[] | undefined): CommitPoint[] =>
  (commits ?? []).flatMap((c) => (str(c.sha) ? [{ sha: str(c.sha)!, merge: Array.isArray(c.parents) && c.parents.length > 1 }] : []));

/** A file of GitHub's compare as the reviewed share reads it. */
export function readBranchFile(f: RawFile): BranchFile | null {
  const path = str(f.filename);
  if (!path) return null;
  const status = str(f.status) ?? 'modified';
  return {
    path,
    from: status === 'renamed' ? str(f.previous_filename) : null,
    status,
    blob: status === 'removed' ? null : str(f.sha),
    additions: count(f.additions),
    deletions: count(f.deletions),
    patch: str(f.patch),
  };
}

interface RawList {
  repository: { refs: { totalCount: number; pageInfo: { hasNextPage: boolean; endCursor: string | null }; nodes: unknown[] } | null } | null;
}

/** The attention sets of `ids` as the dashboard works them out: its rows by ID, then `workOutAttention` with the inputs it keeps. */
export async function dashboardAttention(
  ids: string[],
  signal: AbortSignal,
  deps: { rows?: typeof readRows; engine?: Parameters<typeof workOutAttention>[1]['engine']; kept?: Parameters<typeof workOutAttention>[1]['kept'] } = {},
): Promise<Map<string, PullAttention>> {
  const rows = await (deps.rows ?? readRows)(ids);
  const prs = rows.filter((r): r is DashPr => !!r && r !== 'sso');
  const got = await workOutAttention(prs, { kept: deps.kept ?? sharedAttentionInputs, engine: deps.engine ?? eng.attention, checks: new CheckQueue() }, { fresh: false, run: 'branches', signal });
  return new Map(prs.map((pr) => [pr.id, { pr, members: got.rows.get(pr.id)?.members ?? null }]));
}

export const GITHUB_BRANCHES: BranchesWorld = {
  defaultBranch: (owner, repo, signal) => defaultBranch(owner, repo, { priority: 'visible', signal }),
  async list(owner, repo, after, signal) {
    const r = await graphql<RawList>(REPO_BRANCH_LIST, { owner, repo, after }, { priority: 'visible', signal });
    if (!r.repository) return null;
    const refs = r.repository.refs;
    return {
      branches: (refs?.nodes ?? []).map(readListed).filter((b): b is Branch => b !== null),
      next: refs?.pageInfo.hasNextPage ? refs.pageInfo.endCursor : null,
      total: refs?.totalCount ?? 0,
    };
  },
  async details(owner, repo, def, tips, compare, signal) {
    const names = [...tips.keys()];
    const vars: Record<string, unknown> = { owner, repo, ...(compare ? { default: def } : {}) };
    names.forEach((n, i) => (vars['n' + i] = `refs/heads/${n}`));
    // A ref GitHub has no comparison for (no common history) is an error on its field alone: the rest stands.
    const { data, errors } = await graphqlResult<{ repository: Record<string, unknown> | null }>(branchDetailsQuery(names.length, compare), vars, { priority: 'visible', signal, partial: true });
    // No repository: what GitHub said (a timeout among it) is the failure.
    if (!data?.repository) throw new GraphQLFailure(errors.length ? errors : [{ message: 'GitHub did not answer for the repository.' }], data);
    return new Map(names.map((n, i) => [n, readDetail(data.repository!['r' + i], tips.get(n)!, def)]));
  },
  attention: (ids, signal) => dashboardAttention(ids, signal),
  async compare(owner, repo, base, head, signal) {
    // The first page alone: it lists the files (300 at most), the commit count and the oldest NEWEST_COMMITS commits.
    const body = await rest<{ merge_base_commit?: { sha?: unknown }; files?: RawFile[]; commits?: { sha?: unknown; parents?: unknown[] }[]; total_commits?: unknown }>(`repos/${owner}/${repo}/compare/${base}...${head}?per_page=${NEWEST_COMMITS}`, { priority: 'background', signal });
    const mergeBase = str(body.merge_base_commit?.sha);
    if (!mergeBase) throw new Error('GitHub did not say where the branch branches from its base.');
    return {
      mergeBase,
      files: (body.files ?? []).map(readBranchFile).filter((f): f is BranchFile => f !== null),
      commits: points(body.commits),
      total: count(body.total_commits),
    };
  },
  async comparePage(owner, repo, base, head, page, signal) {
    const b = await rest<{ commits?: { sha?: unknown; parents?: unknown[] }[] }>(`repos/${owner}/${repo}/compare/${base}...${head}?per_page=${NEWEST_COMMITS}&page=${page}`, { priority: 'background', signal });
    return points(b.commits);
  },
  uncachedChanges: (owner, repo, list) => uncachedChanges(owner, repo, list),
  coreLeft: () => coreBudgetLeft(),
  async baseBlobs(owner, repo, at, paths) {
    const got = await blobsOnTrack(owner, repo, [at], paths);
    return new Map([...got].flatMap(([p, blobs]) => (blobs[0] ? [[p, blobs[0]] as const] : [])));
  },
  async trackPairs(owner, repo, points, paths, at) {
    // One page of a commit's file list (one request, as charged); a commit with more is read as not listed whole.
    const found = await blobsOnTrackPerPath(owner, repo, points, paths, { at, source: githubSource(owner, repo, { commitPages: 1 }) });
    return [...found.values()].flatMap((f) => [...f.blobs].flatMap(([path, blobs]) => blobs.map((blob) => ({ path, blob }))));
  },
  async kept(subject) {
    // As the Files view opens the subject: base…head, the viewer's diff algorithm, whitespace shown.
    const key = statusKeyOf(subjectKey(subject), { from: null, to: null, algorithm: algorithmOf(prefs.value.diffAlgorithm), ignoreWhitespace: false });
    const record = await idbStatusStore.read(key).catch(() => undefined);
    return new Map(Object.entries(record?.files ?? {}));
  },
  listen: (topics, handler) => {
    const live = new LiveEvents(topics, handler);
    return () => live.close();
  },
};

/** What a branch's reviewed share is computed from, with what the compare page kept. */
interface Read extends ShareInputs {
  kept: ReadonlyMap<string, FileStatus>;
  /** The lookup of earlier versions was skipped (budget, or the hour's REST budget low): read again next view. */
  skipped: boolean;
  /** The blobs the compare page asks a kept file's marks on (along the comparison's commits), so its kept status's marks can be checked. */
  trackPairs: { path: string; blob: string }[];
}

/** Comparisons read, by `owner/repo@<base sha>...<tip sha>`: an answer by SHAs never changes. */
const compared = new LruMap<string, Promise<Read>>(500);

/** Forget what was read (another account signed in; tests). */
export function forgetBranches() {
  compared.clear();
  yoursWithoutPull.clear();
}
onIdentityChange(forgetBranches);

/**
 * How many of the viewer's branches have no pull request, in each
 * repository whose branches were read this session (`owner/repo` →
 * count, and whether more branches exist than were read: past the
 * listing's cap, or while it is read): the dashboard's quiet line.
 */
class YoursWithoutPull {
  counts: ReadonlyMap<string, { count: number; partial: boolean }> = $state.raw(new Map());
  set(repo: string, c: { count: number; partial: boolean }) {
    const was = this.counts.get(repo);
    if (was?.count !== c.count || was.partial !== c.partial) this.counts = new Map(this.counts).set(repo, c);
  }
  clear() {
    if (this.counts.size) this.counts = new Map();
  }
}
export const yoursWithoutPull = new YoursWithoutPull();

const viewerLogin = () => session.user?.login ?? '';

export class BranchesStore {
  /** `empty`: the repository has no branch; `nodefault`: the default branch GitHub names does not exist. */
  status: 'loading' | 'ready' | 'empty' | 'nodefault' | 'notfound' | 'error' = $state('loading');
  failure: Failure | null = $state(null);
  notFound: string | null = $state(null);
  defaultBranch: string | null = $state(null);
  /** Every branch listed, newest push first, with its details once read. */
  branches: readonly Branch[] = $state.raw([]);
  total = $state(0);
  /** More branches exist than the listing read (past MAX_LIST_PAGES). */
  truncated = $state(false);
  /** More pages of the listing are being read: the rows shown are those read so far. */
  listing = $state(false);
  /** Each open pull request's attention set, by node ID, once worked out. */
  attention: ReadonlyMap<string, PullAttention> = $state.raw(new Map());
  private viewer: () => string = viewerLogin;
  private now: () => number = Date.now;
  readonly groups: { group: BranchGroup; branches: Branch[] }[] = $derived(
    groupBranches(this.branches, { defaultBranch: this.defaultBranch ?? '', viewer: this.viewer(), now: this.now() }),
  );
  readonly marks: MarksStore;
  /** The server keeps no marks for this repository (no installation covers it): no share is known. */
  unrecorded = $state(false);
  private reads: ReadonlyMap<string, Read> = $state.raw(new Map());
  private failed: ReadonlySet<string> = $state.raw(new Set());
  private detailFailures: ReadonlySet<string> = $state.raw(new Set());
  private asked = new Set<string>();
  private onScreen = new Set<string>();
  private detailing = new Set<string>();
  private queued = new Set<string>();
  private flushing: Promise<void> | null = null;
  private attentionAsked = new Set<string>();
  private flying = new Set<Promise<unknown>>();
  private memo = new WeakMap<readonly unknown[], Map<string, ReviewedShare>>();
  private abort = new AbortController();
  private stop: () => void = () => {};
  private disposed = false;
  private world: BranchesWorld;

  constructor(
    readonly owner: string,
    readonly repo: string,
    opts: { world?: BranchesWorld; viewer?: () => string; now?: () => number } = {},
  ) {
    this.world = opts.world ?? GITHUB_BRANCHES;
    if (opts.viewer) this.viewer = opts.viewer;
    if (opts.now) this.now = opts.now;
    this.marks = new MarksStore(owner, repo, null, () => (this.unrecorded = true));
  }

  private get repoKey(): string {
    return `${this.owner}/${this.repo}`;
  }

  /** The default branch's row; null until listed, or when it does not exist. */
  get defaultRef(): Branch | null {
    return this.branches.find((b) => b.name === this.defaultBranch) ?? null;
  }

  /** Listen for marks notices, and read the listing. */
  open(): Promise<void> {
    const repo = this.repoKey.toLowerCase();
    const topics = [`repo:${repo}`];
    if (session.user) topics.push(`user:${session.user.id}`);
    this.stop = this.world.listen(topics, (_topic, notice) => {
      const ours = typeof notice?.repo === 'string' && notice.repo.toLowerCase() === repo;
      if (ours && (notice.kind === 'marks' || notice.kind === 'flags')) void this.marks.onNotice(notice as MarksNotice);
    });
    void this.marks.load();
    return this.load();
  }

  private track<T>(p: Promise<T>): Promise<T> {
    this.flying.add(p);
    void p.finally(() => this.flying.delete(p)).catch(() => {});
    return p;
  }

  /**
   * Read the default branch and the listing again, showing each page as it
   * arrives (sorted with those before), then the details the groups need:
   * the default branch's and the viewer's recent branches' as each page
   * lands, then the other recent ones', newest first, EAGER_DETAILS in all.
   */
  async load(): Promise<void> {
    this.status = 'loading';
    this.failure = null;
    this.eager = new Set();
    try {
      const signal = this.abort.signal;
      const def = await this.world.defaultBranch(this.owner, this.repo, signal);
      let listed: Branch[] = [];
      let after: string | null = null;
      let pages = 0;
      const cutoff = this.now() - STALE_DAYS * 86_400_000;
      const recent = (b: Branch) => (Date.parse(b.tip.committedDate) || 0) >= cutoff;
      const mine = (b: Branch) => [b.tip.author, b.tip.committer].some((l) => l !== null && sameLogin(l, this.viewer()));
      do {
        const page: BranchList | null = await this.world.list(this.owner, this.repo, after, signal);
        if (this.disposed) return;
        if (!page) {
          this.notFound = `GitHub doesn't show ${this.repoKey} to you.`;
          this.status = 'notfound';
          return;
        }
        const names = new Set(listed.map((b) => b.name));
        listed = [...listed, ...page.branches.filter((b) => !names.has(b.name))].sort(byNewest);
        after = page.next;
        pages++;
        this.defaultBranch = def;
        // What this tab read of each branch (its details) is kept as more of the listing arrives.
        const read = new Map(this.branches.map((b) => [b.name, b]));
        this.branches = listed.map((b) => read.get(b.name) ?? b);
        this.total = page.total;
        this.listing = after !== null && pages < MAX_LIST_PAGES;
        if (this.listing || listed.length) this.status = 'ready';
        for (const b of page.branches) if (b.name === def || (recent(b) && mine(b))) this.queueEager(b.name);
        this.count();
        void this.track(this.flush());
      } while (this.listing);
      this.truncated = after !== null;
      this.status = !listed.length ? 'empty' : listed.some((b) => b.name === def) ? 'ready' : 'nodefault';
      this.count();
      if (this.status === 'empty') return;
      for (const b of listed) if (recent(b)) this.queueEager(b.name);
      await this.track(this.flush());
    } catch (e) {
      if (this.disposed) return;
      this.listing = false;
      if (e instanceof CompareNotFound || (e instanceof HttpError && e.status === 404)) {
        this.notFound = e instanceof CompareNotFound ? e.message : `GitHub doesn't show ${this.repoKey} to you.`;
        this.status = 'notfound';
      } else {
        this.failure = failureOf(e);
        this.status = 'error';
      }
    }
  }

  /** Branches whose details were queued without waiting for their rows. */
  private eager = new Set<string>();

  /** Queue `name`'s details without waiting for its row, EAGER_DETAILS at most. */
  private queueEager(name: string) {
    if (this.eager.has(name) || this.eager.size >= EAGER_DETAILS) return;
    this.eager.add(name);
    this.queued.add(name);
  }

  /**
   * Tell the dashboard how many of the viewer's branches have no pull
   * request: only those whose details are read (one not read yet may have
   * one). The count is partial while the listing is read, past its cap, or
   * while any branch the 'yours' group holds has no details (in flight,
   * failed, or not asked yet).
   */
  private count() {
    const yours = this.groups.find((g) => g.group === 'yours')?.branches ?? [];
    const read = yours.filter((b) => b.detail !== null);
    yoursWithoutPull.set(this.repoKey, { count: read.length, partial: this.listing || this.truncated || read.length < yours.length });
  }

  /** Read the details of the branches queued, a batch at a time. */
  private flush(): Promise<void> {
    if (!this.flushing)
      this.flushing = (async () => {
        await Promise.resolve();
        while (this.queued.size && !this.disposed) {
          const names = [...this.queued].slice(0, BRANCH_DETAILS_BATCH);
          for (const n of names) {
            this.queued.delete(n);
            this.detailing.add(n);
          }
          await this.detail(names, true);
          for (const n of names) this.detailing.delete(n);
        }
        this.flushing = null;
      })();
    return this.flushing;
  }

  /**
   * Read the details of `names`. A batch GitHub ran out of time on (a
   * comparison is the costly part) is asked again in halves, then a single
   * branch without its comparison; any other failure (offline, signed out,
   * GitHub failing) marks the batch failed at once, for Try again, with no
   * more requests. A branch GitHub no longer has leaves the list.
   */
  private async detail(names: string[], compare: boolean): Promise<void> {
    const tips = new Map(this.branches.filter((b) => names.includes(b.name)).map((b) => [b.name, b.tip.oid]));
    if (!tips.size || !this.defaultBranch) return;
    try {
      const got = await this.world.details(this.owner, this.repo, this.defaultBranch, tips, compare, this.abort.signal);
      if (this.disposed) return;
      this.branches = this.branches.flatMap((b) => {
        const d = got.get(b.name);
        if (!d) return [b];
        return d.gone ? [] : [{ ...b, detail: d }];
      });
      if (this.detailFailures.size) this.detailFailures = new Set([...this.detailFailures].filter((n) => !got.has(n)));
      this.count();
      void this.track(this.readAttention());
      for (const n of got.keys()) if (this.onScreen.has(n)) this.readShare(n);
    } catch (e) {
      if (this.disposed) return;
      const slow = failureOf(e).cause === 'github_slow';
      if (slow && names.length > 1) {
        const half = Math.ceil(names.length / 2);
        await this.detail(names.slice(0, half), compare);
        await this.detail(names.slice(half), compare);
      } else if (slow && compare) await this.detail(names, false);
      else this.detailFailures = new Set([...this.detailFailures, ...names]);
      this.count();
    }
  }

  /** The attention sets of the open pull requests not asked about yet. */
  private async readAttention(): Promise<void> {
    const ids = this.branches.flatMap((b) => (b.detail?.pull && !this.attentionAsked.has(b.detail.pull.id) ? [b.detail.pull.id] : []));
    if (!ids.length) return;
    for (const id of ids) this.attentionAsked.add(id);
    try {
      const got = await this.world.attention(ids, this.abort.signal);
      if (!this.disposed && got.size) this.attention = new Map([...this.attention, ...got]);
    } catch {
      // Whose turn stays unknown (the row says nothing of it); asked again when the tab is opened again.
    }
  }

  /** The details of `b` could not be read. */
  detailFailed(b: Branch): boolean {
    return this.detailFailures.has(b.name);
  }

  /** Read again the details that could not be read. */
  retryDetails(): Promise<void> {
    for (const n of this.detailFailures) this.queued.add(n);
    this.detailFailures = new Set();
    return this.track(this.flush());
  }

  /** The base `b` is measured from: its pull request's base, else the default branch's tip; null where it has none. */
  private baseOf(b: Branch): string | null {
    if (b.detail?.pull?.baseOid) return b.detail.pull.baseOid;
    return this.defaultRef?.tip.oid ?? null;
  }

  /** The key of `b`'s comparison with its base; null where there is nothing to read. */
  private keyOf(b: Branch): string | null {
    const base = this.baseOf(b);
    if (!base || !b.detail || b.name === this.defaultBranch || ((b.detail.aheadBy === 0 || b.detail.merged) && !b.detail.pull)) return null;
    return `${this.repoKey.toLowerCase()}@${base}...${b.tip.oid}`;
  }

  /** `b`'s row is on screen: read its details if they are not, then what its reviewed share needs, once per tip. */
  see(b: Branch): void {
    if (this.disposed) return;
    this.onScreen.add(b.name);
    if (!b.detail) {
      if (!this.detailing.has(b.name) && !this.detailFailures.has(b.name)) {
        this.queued.add(b.name);
        void this.track(this.flush());
      }
      return;
    }
    this.readShare(b.name);
  }

  private readShare(name: string) {
    const b = this.branches.find((x) => x.name === name);
    const key = b && this.keyOf(b);
    if (!b || !key || this.asked.has(key)) return;
    this.asked.add(key);
    if (this.failed.has(key)) this.failed = new Set([...this.failed].filter((k) => k !== key));
    let read = compared.get(key);
    if (!read) {
      read = this.readInputs(b, this.baseOf(b)!);
      compared.set(key, read);
    }
    void this.track(
      read.then(
        (inputs) => {
          if (this.disposed) return;
          this.reads = new Map(this.reads).set(key, inputs);
          // A lookup skipped for budget is not kept for the page: the next view may make it.
          if (inputs.skipped) compared.delete(key);
          this.marks.show([...sidesShown(inputs.files, inputs.oldBlobs), ...inputs.trackPairs]);
        },
        () => {
          compared.delete(key);
          this.asked.delete(key);
          if (!this.disposed) this.failed = new Set([...this.failed, key]);
        },
      ),
    );
  }

  private async readInputs(b: Branch, base: string): Promise<Read> {
    const pull = b.detail?.pull ?? null;
    const subject: Subject = pull
      ? { kind: 'pull', owner: this.owner, repo: this.repo, number: pull.number }
      : { kind: 'compare', owner: this.owner, repo: this.repo, base: this.defaultBranch, head: b.name, headRepo: null };
    const [cmp, kept] = await Promise.all([this.world.compare(this.owner, this.repo, base, b.tip.oid, this.abort.signal), this.world.kept(subject).catch(() => new Map<string, FileStatus>())]);
    const paths = oldSides(cmp.files);
    const oldBlobs = paths.length ? await this.world.baseBlobs(this.owner, this.repo, cmp.mergeBase, paths) : new Map<string, string>();
    let unlisted: Unlisted = null;
    if (cmp.files.length >= LISTED_FILES_MAX) {
      const listed = cmp.files.reduce((n, f) => n + f.additions + f.deletions, 0);
      unlisted = pull && pull.changedFiles > cmp.files.length ? { lines: pull.additions + pull.deletions - listed } : pull ? null : 'unknown';
    }
    const { trackPairs, skipped } = await this.earlierVersions(cmp, base, b.tip.oid, kept);
    return { files: cmp.files, oldBlobs, unlisted, kept, trackPairs, skipped };
  }

  /** REST requests the lookup of earlier versions may still send in this view of the tab (CHANGE_READ_BUDGET). */
  private readsLeft = CHANGE_READ_BUDGET;
  /** What is left of the view's budget for that lookup. For tests. */
  get lookupBudgetLeft(): number {
    return this.readsLeft;
  }

  /**
   * The marks on the earlier versions of the comparison's files, asked where
   * the compare page asks them (their blobs along the commits): to check a
   * kept status against, and to know when marks there, which only the
   * compare page's line maps carry, make the figure a lower bound. Bounded
   * three ways: only the paths the compare or pull request page kept a
   * status for (the kept record, read from IndexedDB: no GitHub call; most
   * rows have none, and make no lookup); only the comparison's newest
   * NEWEST_COMMITS commits, where the version last reviewed sits (the
   * compare's own first page, and its last pages: `lastPages`); and at most
   * CHANGE_READ_BUDGET REST requests a view, every one it sends charged (the
   * pages read, each change list no page kept, two for a merge), and none
   * while less than CORE_FLOOR of the hour's REST budget is left. Skipped
   * past those, the figure is said to be counted from these versions.
   */
  private async earlierVersions(cmp: Compared, base: string, tip: string, kept: ReadonlyMap<string, FileStatus>): Promise<{ trackPairs: { path: string; blob: string }[]; skipped: boolean }> {
    const keptPaths = new Set([...kept.keys()].map((id) => id.slice(0, id.indexOf('|'))));
    const paths = cmp.files.filter((f) => keptPaths.has(f.path) || (f.from !== null && keptPaths.has(f.from))).map((f) => f.path);
    if (!paths.length) return { trackPairs: [], skipped: false };
    const left = this.world.coreLeft();
    if (left !== null && left < CORE_FLOOR) return { trackPairs: [], skipped: true };
    try {
      // Every request the lookup sends is charged: the pages holding the newest commits (page 1 is the compare's
      // own), then each change list no page kept, two for a merge.
      const pages = lastPages(cmp.total);
      const toRead = pages.filter((p) => p !== 1);
      if (toRead.length > this.readsLeft) return { trackPairs: [], skipped: true };
      this.readsLeft -= toRead.length;
      const read = await Promise.all(toRead.map((p) => this.world.comparePage(this.owner, this.repo, base, tip, p, this.abort.signal)));
      const commits = pages.flatMap((p) => (p === 1 ? cmp.commits : read[toRead.indexOf(p)])).slice(-NEWEST_COMMITS);
      const merges = new Set(commits.filter((c) => c.merge).map((c) => c.sha));
      const uncached = await this.world.uncachedChanges(this.owner, this.repo, commits.map((c) => c.sha));
      const reads = uncached.reduce((n, sha) => n + (merges.has(sha) ? 2 : 1), 0);
      if (reads > this.readsLeft) return { trackPairs: [], skipped: true };
      this.readsLeft -= reads;
      return { trackPairs: await this.world.trackPairs(this.owner, this.repo, [cmp.mergeBase, ...commits.map((c) => c.sha)], paths, tip), skipped: false };
    } catch {
      return { trackPairs: [], skipped: true };
    }
  }

  /** How much of `b` the viewer has reviewed; null until read (and for the default branch, or with no base to measure from). */
  share(b: Branch): ReviewedShare | null {
    if (b.name === this.defaultBranch || !b.detail || !this.baseOf(b)) return null;
    if (b.detail.aheadBy === 0 && !b.detail.pull) return { done: 0, total: 0, known: true, firstFiles: false, lowerBound: false };
    const key = this.keyOf(b);
    const read = key ? this.reads.get(key) : undefined;
    if (!key || !read) return null;
    // Counted once per marks answer: a row re-renders on every one.
    const anchors = this.marks.anchors;
    let byKey = this.memo.get(anchors);
    if (!byKey) this.memo.set(anchors, (byKey = new Map()));
    const hit = byKey.get(key);
    if (hit) return hit;
    const counted = reviewedShare(read, anchors, (p, blob) => this.marks.answered(p, blob), read.kept);
    const s: ReviewedShare = read.skipped ? { ...counted, theseVersions: true } : counted;
    if (s.known) byKey.set(key, s);
    return s;
  }

  /** The last read of `b`'s share failed. */
  shareFailed(b: Branch): boolean {
    const key = this.keyOf(b);
    return !!key && this.failed.has(key);
  }

  /** Read `b`'s share again after it failed. */
  retryShare(b: Branch): void {
    this.readShare(b.name);
  }

  /** Resolves once every read started so far has settled. For tests. */
  async reading(): Promise<void> {
    while (this.flying.size) await Promise.allSettled([...this.flying]);
  }

  dispose() {
    this.disposed = true;
    this.abort.abort();
    this.stop();
    this.marks.dispose();
  }
}
